<template>
  <div class="w-100">
    <proa-card v-if="user" class="h-100 d-flex flex-column" no-padding>
      <template v-slot:header>
        <div class="d-flex">
          <proa-back-button
            @onClick="onClickBackButton"
          />
          <div class="avatar-wrapper">
            <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" />
          </div>
          <div class="user-information-wrapper">
            <proa-heading sub>{{ user.meta.displayName }}</proa-heading>
          </div>
        </div>
      </template>
      <div class="member-profile">
        <div class="member-body">
          <table class="table-property">
            <tbody>
              <tr>
                <th>{{ $t('models.user.attributes.emailShort') }}</th>
                <td>{{ user.email }}</td>
              </tr>
              <tr>
                <th>{{ $t('models.user.attributes.phone') }}</th>
                <td>{{ user.meta.phone }}</td>
              </tr>
              <tr>
                <th valign="top">{{ $t('models.user.attributes.publicDescription') }}</th>
                <td class="text-pre-wrap" valign="top">{{ user.meta.publicDescription }}</td>
              </tr>
              <tr>
                <th>{{ $t('models.user.attributes.role') }}</th>
                <td>{{ $t(`common.roleType.${currentOrganization.adminUserId == user.id ? 'admin' : user.currentOrganizationRole().roleType}`) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="member-footer">
        <span v-if="currentOrganization.adminUserId == userId" class="text-secondary">
          {{ $t('pages.showManagementMembers.adminUserMessage') }}
        </span>
        <template v-else>
          <proa-button
            class="mr-2"
            @click.native="showUpdateUserOrganizationRoleModal"
          >
            {{ $t('pages.showManagementMembers.changeRole') }}
          </proa-button>
          <proa-button
            @click.native="deleteManagedUserModal = true"
            variant="secondary"
          >
            {{ $t('pages.showManagementMembers.removeFromOrganization') }}
          </proa-button>
        </template>
      </div>
    </proa-card>
    <proa-modal
      v-if="user"
      :modal="updateUserOrganizationRoleModal"
      :title="$t('common.modals.updateUserOrganizationRoleModal.title')"
      @close="() => { if (!updateUserOrganizationRoleRequesting) { updateUserOrganizationRoleModal = false } }"
    >
      <proa-select
        v-model="updateUserOrganizationRole"
        :options="roleOptions"
      />
      <template v-slot:footer>
        <proa-button
          @click.native="updateUserOrganizationRoleModal = false"
          :disabled="updateUserOrganizationRoleRequesting"
          class="mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="onRequestUpdateUserOrganizationRole"
          :disabled="user.currentOrganizationRoleId == updateUserOrganizationRole"
          :loading="updateUserOrganizationRoleRequesting"
          variant="primary"
        >
          {{ $t('common.change') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      v-if="user"
      :modal="deleteManagedUserModal"
      :title="$t('common.confirm')"
      @close="() => { if (!deleteManagedUserRequesting) { deleteManagedUserModal = false } }"
    >
      <proa-heading sub>{{ $t('common.modals.deleteManagedUserModal.confirmMessage') }}</proa-heading>
      <template v-slot:footer>
        <proa-button
          @click.native="deleteManagedUserModal = false"
          :disabled="deleteManagedUserRequesting"
          class="mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="onConfirmedDeleteManagedUser"
          :loading="deleteManagedUserRequesting"
          variant="secondary"
        >
          {{ $t('common.delete') }}
        </proa-button>
      </template>
    </proa-modal>
  </div>
</template>

<script>
import i18n from '@/i18n'
import { mapGetters, mapActions } from 'vuex'
import ManagedUser from '@/store/models/admin/ManagedUser'
import Role from '@/store/models/Role'
import Organization from '@/store/models/Organization'

export default {
  name: 'ShowManagementMembers',
  computed: {
    userId: function() { return this.$route.params.id },
    user: function() { return ManagedUser.find(this.userId) },
    roleOptions: function() {
      return Role.all().filter((role) => {
        return role.roleType !== 'temporaryGuest'
      }).map((role) => {
        const currentRole = role.id == this.user.currentOrganizationRoleId
        return {
          label: i18n.tc(`common.roleType.${role.roleType}`),
          value: role.id,
          selected: currentRole,
          disabled: currentRole
        }
      })
    },
    ...mapGetters({
      currentOrganization: 'auth/currentOrganization'
    })
  },
  data() {
    return {
      updateUserOrganizationRole: null,
      updateUserOrganizationRoleModal: false,
      updateUserOrganizationRoleRequesting: false,
      deleteManagedUserModal: false,
      deleteManagedUserRequesting: false
    }
  },
  methods: {
    onClickBackButton: function() {
      this.$router.push({ name: 'indexManagementMembers' })
    },
    showUpdateUserOrganizationRoleModal: function() {
      this.updateUserOrganizationRole = this.user.currentOrganizationRoleId
      this.updateUserOrganizationRoleModal = true
    },
    async onRequestUpdateUserOrganizationRole() {
      this.updateUserOrganizationRoleRequesting = true
      await this.apiPutManagedUserOrganization({
        userId: this.userId,
        attributes: {
          roleId: this.updateUserOrganizationRole
        }
      })
      this.updateUserOrganizationRoleRequesting = this.updateUserOrganizationRoleModal = false
    },
    async onConfirmedDeleteManagedUser() {
      this.deleteManagedUserRequesting = true
      await this.apiDeleteManagedUser(this.userId)
      Organization.update({
        id: this.currentOrganization.id,
        currentSeats: this.currentOrganization.currentSeats - 1
      })
      this.onClickBackButton()
    },
    ...mapActions({
      apiGetManagedUser: 'entities/managedUsers/show',
      apiDeleteManagedUser: 'entities/managedUsers/destroy',
      apiPutManagedUserOrganization: 'managedUserOrganization/update',
    })
  },
  async created() {
    await this.apiGetManagedUser(this.userId)
    if (!this.user) {
      return
    }
    this.updateUserOrganizationRole = this.user.currentOrganizationRoleId
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.avatar-wrapper {
  margin: 0;

  @include mq(md) {
    margin: 20px 20px 30px 30px;
  }

  img {
    width: 60px !important;
    height: 60px !important;

    @include mq(md) {
      width: 80px !important;
      height: 80px !important;
    }
  }
}

.user-information-wrapper {
  margin-top: 0;

  @include mq(md) {
    margin-top: 20px;
  }
}

.member-profile {
  height: 100%;
  display: flex;
  flex-direction: column;

  .member-body {
    flex-grow: 1;
    padding: 20px;

    .table-property {
      margin-bottom: 25px;

      th, td {
        padding: 0 15px 10px 0;
        font-size: 15px;

        @include mq(md) {
          padding: 0 17px 15px 0;
          font-size: 14px;
        }
      }

      th {
        font-weight: 100;
        color: $secondary-color;
        min-width: 75px;

        @include mq(md) {
          font-weight: 600;
          color: #000;
          min-width: 73px;
        }
      }
    }
  }
}

.member-footer {
  display: flex;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid $dark-border-color;

  @include mq(md) {
    justify-content: flex-end;
  }
}
</style>
